<script setup lang="ts">
import UiAccordion from '@boats/ui/components/UiAccordion.vue'
import UiTableInfo from '@boats/ui/components/UiTableInfo.vue'
import UiTableInfoCell from '@boats/ui/components/UiTableInfoCell.vue'
import UiTableInfoRow from '@boats/ui/components/UiTableInfoRow.vue'
import { type InfoData } from '~/queries'

const props = defineProps<{
  breadcrumbs: InfoData['breadcrumbs']
  product: InfoData['product']
  refresh: () => Promise<unknown>
}>()

const { $i18n, $analyticsManager } = useNuxtApp()
const { t } = $i18n

const favourites = useFavouritesStore()
const modals = useModalsStore()

const breadcrumbsItems = computed(() =>
  props.breadcrumbs.map((item) => ({
    title: item.title,
    href: cleanPath('/', item.path)
  }))
)

onMounted(() => {
  $analyticsManager.trackProduct(props.product)
})

const faqs = computed(() => {
  const faqsArray = []

  if (props.product?.type === 'football_match') {
    if (props.product?.additionalDescription) {
      faqsArray.push({
        title: t('page.product.additionalDescription.title'),
        content: props.product.additionalDescription
      })
    }

    if (props.product?.additionalNotes) {
      faqsArray.push({
        title: t('page.product.additionalNotes.title.football'),
        content: props.product.additionalNotes
      })
    }

    if (props.product?.notes) {
      faqsArray.push({
        title: t('page.product.notes.title.football'),
        content: props.product.notes
      })
    }
  } else {
    if (props.product?.notes) {
      faqsArray.push({
        title: t('page.product.notes.title'),
        content: props.product.notes
      })
    }

    if (props.product?.additionalDescription) {
      faqsArray.push({
        title: t('page.product.additionalDescription.title'),
        content: props.product.additionalDescription
      })
    }

    if (props.product?.additionalNotes) {
      faqsArray.push({
        title: t('page.product.additionalNotes.title'),
        content: props.product.additionalNotes
      })
    }
  }

  const faqs = props.product?.faqs

  faqs
    ?.sort((a, b) => a.position - b.position)
    .forEach((faq) => {
      faqsArray.push({
        title: faq.question,
        content: faq.answer
      })
    })

  return faqsArray
})

const related = computed(() => null)

const hasTickets = computed(() => {
  if (!props.product) return false

  const now = new Date()

  if (
    ['football_match', 'event'].includes(props.product.type) &&
    props.product.startDate
  ) {
    return new Date(props.product.startDate) >= now
  }

  return true
})

const goToTickets = () => {
  const rect = document?.getElementById('tickets')?.getBoundingClientRect()

  if (!rect) return

  const top = window.scrollY + rect.top - 80

  window.scrollTo({ top, behavior: 'smooth' })
}
</script>

<template>
  <div>
    <AppContainer v-if="product" class="my-10 md:my-20">
      <div class="row">
        <div class="col-lg-5 mb-3 md:mb-6 lg:mb-0 xl:pr-16">
          <AppCarouselMedia :object="product" type="product" />
          <div class="hidden lg:block">
            <AppButton
              variant="outlined"
              full-width
              class="mb-5"
              @click="modals.openShareModal"
            >
              {{ $t('string.Share-this-page') }}
            </AppButton>

            <div
              class="flex items-start rounded border border-gray-200 p-3 md:p-6"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 36 42"
                class="mr-3 w-[1.875rem] flex-shrink-0 md:mr-6 md:w-9"
              >
                <path
                  d="M.68,7a1,1,0,0,1,.48-.36h0l16.5-6h0a1,1,0,0,1,.68,0h0l16.5,6h0a1,1,0,0,1,.48.36,1,1,0,0,1,.18.57h0V21c0,7-4.06,12-8.28,15.3a34.83,34.83,0,0,1-8.88,5.06h0a1,1,0,0,1-.68,0h0A34.68,34.68,0,0,1,8.79,36.3C4.56,33,.5,28,.5,21V7.55A1,1,0,0,1,.68,7"
                  fill="#e0f0ff"
                  stroke="#121318"
                />
                <path
                  d="M11.67,19.37l4.17,4.06,9.46-10.7"
                  fill="none"
                  stroke="#121318"
                  stroke-width="2"
                />
              </svg>

              <div class="ticket-supplier-description" v-html="product.ticketSupplierDescription"></div>
            </div>
          </div>
        </div>

        <div class="col-lg-7 col-xl-6">
          <div class="hidden md:block">
            <AppBreadcrumbs class="!mb-3" :items="breadcrumbsItems" />
          </div>

          <h1 class="mb-1 md:mb-3 general-product-title">{{ product.title }}</h1>

          <div
            v-if="product.intro"
            class="lead clean-cms-styles mb-6 md:mb-10"
            v-html="product.intro"
          ></div>

          <UiTableInfo class="mb-6 md:mb-10">
            <UiTableInfoRow v-if="product.type === 'football_match'">
              <UiTableInfoCell v-if="product.startDate">
                <template #icon>
                  <IconClock />
                </template>
                {{ formatDate(product.startDate) }}
              </UiTableInfoCell>

              <UiTableInfoCell v-if="product.startDate && product.startTime">
                <template #icon>
                  <IconClock />
                </template>
                {{
                  formatTime([product.startDate, product.startTime].join(' '))
                }}
              </UiTableInfoCell>

              <UiTableInfoCell>
                <template #icon>
                  <IconTicket />
                </template>
                Mobile E-ticket
              </UiTableInfoCell>
            </UiTableInfoRow>

            <UiTableInfoRow v-if="product.venue.name || product.venue.address">
              <UiTableInfoCell :href="product.venue.googleMapsLink || undefined">
                <template #icon>
                  <IconMapPin />
                </template>
                {{ product.venue.name || product.venue.address }}
              </UiTableInfoCell>
            </UiTableInfoRow>
            <div v-if="product.keyFeatureLabels?.length" class="gap-y-3 py-2 flex flex-wrap">
              <KeyLabelFeature
                v-for="(label, i) in product.keyFeatureLabels"
                :key="'key-label-feature' + i + product.id"
                :name="label.name"
                :colour="label.colour"
                class="mr-2"
              />
            </div>
            <UiTableInfoRow v-if="product.type === 'show'">
              <UiTableInfoCell>
                <template #icon>
                  <IconTicket />
                </template>
                Mobile E-ticket
              </UiTableInfoCell>
            </UiTableInfoRow>
          </UiTableInfo>

          <template v-if="product.startPrice && hasTickets">
            <div
              class="mb-6 flex flex-col items-center overflow-hidden rounded-sm border border-black md:hidden"
            >
              <div
                class="flex min-h-11 w-full flex-1 items-center border-b border-black px-3 text-sm text-black"
              >
                <span class="self-center">{{ $t('string.Tickets-from') }}</span>
                <span class="h2 alt ml-auto">{{
                    formatCurrency(product.startPrice)
                  }}</span>
              </div>

              <AppButton class="!rounded-none" full-width @click="goToTickets">
                {{ $t('string.Book-tickets') }}
              </AppButton>
            </div>

            <div
              class="mb-10 hidden items-center overflow-hidden rounded-sm border border-black md:flex"
            >
              <div class="flex-1 px-5 text-black">
                {{ $t('string.Tickets-from') }}
                <span class="h4">{{ formatCurrency(product.startPrice) }}</span>
              </div>

              <div class="w-px self-stretch bg-black"></div>

              <AppButton class="!rounded-none" @click="goToTickets">{{
                  $t('string.Book-tickets')
                }}
              </AppButton>
            </div>
          </template>

          <template v-if="product.body">
            <h4 class="mb-2 md:mb-5">{{ $t('page.product.body.title') }}</h4>

            <AppPreviewContent class="mb-6 md:mb-10">
              <div class="cms-content clean-cms-styles" v-html="product.body"></div>
            </AppPreviewContent>
          </template>

          <UiAccordion v-if="faqs.length" :items="faqs" />
          <div class="mt-6 flex items-start rounded border border-gray-200 p-3 md:mt-10 md:p-6 lg:hidden">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 36 42"
              class="mr-3 w-[1.875rem] flex-shrink-0 md:mr-6 md:w-9"
            >
              <path
                d="M.68,7a1,1,0,0,1,.48-.36h0l16.5-6h0a1,1,0,0,1,.68,0h0l16.5,6h0a1,1,0,0,1,.48.36,1,1,0,0,1,.18.57h0V21c0,7-4.06,12-8.28,15.3a34.83,34.83,0,0,1-8.88,5.06h0a1,1,0,0,1-.68,0h0A34.68,34.68,0,0,1,8.79,36.3C4.56,33,.5,28,.5,21V7.55A1,1,0,0,1,.68,7"
                fill="#e0f0ff"
                stroke="#121318"
              />
              <path
                d="M11.67,19.37l4.17,4.06,9.46-10.7"
                fill="none"
                stroke="#121318"
                stroke-width="2"
              />
            </svg>

            <div class="ticket-supplier-description" v-html="product.ticketSupplierDescription"></div>
          </div>
        </div>
      </div>
    </AppContainer>

    <section id="tickets" class="bg-gray-100">
      <AppDivider class="my-8 md:my-16" strong />

      <AppContainer class="my-10 md:my-20">
        <div class="row">
          <div class="col-lg-5 mb-6 lg:mb-0">
            <h2 class="alt">{{ $t('string.Book-tickets') }}</h2>

            <AppImage
              v-if="product?.venue.stadiumImage"
              :src="product.venue.stadiumImage"
              :alt="$t('string.Seating-Map')"
              class="mt-6 md:mt-16"
            />
          </div>

          <div class="col-lg-7 col-xl-6">
            <ReservationTour
              v-if="product?.type === 'attraction' && product?.source === 'tourcms'"
              :product="product"
            />
            <AppTicketsAttraction
              v-else-if="product?.type === 'attraction'"
              :product="product"
            />

            <AppTicketsEvent
              v-else-if="product?.type === 'event'"
              :product="product"
            />

            <AppTicketsShow
              v-else-if="product?.type === 'show'"
              :product="product"
            />

            <AppTicketsTransport
              v-else-if="product?.type === 'transport'"
              :product="product"
            />
          </div>
        </div>
      </AppContainer>

      <AppDivider class="my-8 md:my-16" strong />
    </section>

    <AppContainer class="my-10 md:my-20">
      <template v-if="related?.length">
        <section class="row">
          <div class="col-lg-3 mb-6 lg:mb-0">
            <h2 class="alt">{{ $t('page.category.related.title') }}</h2>
          </div>

          <div class="col-lg-2"></div>

          <div class="col-lg-7 col-xl-6">
            <template v-for="item in related" :key="item.id">
              <AppCardInline
                :id="item.id"
                type="product"
                :to="cleanPath('/', item.path)"
                :title="item.title"
                :image="{ src: item.image, alt: item.title }"
                :favourited="item.favourited"
                :refetch="relatedRefresh"
              >
              </AppCardInline>

              <AppDivider class="my-4 md:my-6" />
            </template>
          </div>
        </section>

        <AppDivider class="my-8 md:my-16" strong />
      </template>

      <AppNewsletter />
    </AppContainer>
  </div>
</template>

<style scoped>
.ticket-supplier-description {
  min-width: 0;
  overflow-wrap: anywhere;
}

::v-deep(.ticket-supplier-description h4) {
  margin-bottom: 0.25rem;
  font-size: 0.875rem;
}

@media (min-width: 768px) {
  ::v-deep(.ticket-supplier-description h4) {
    font-size: 1.25rem;
  }
}

.general-product-title {
  font-size: 3rem;
}


</style>
